import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestIdleCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestIdleCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantScript = html.querySelector('variant-picker script[data-selected-variant]') || html.querySelector('variant-picker script[type="application/json"]');
        const textContent = variantScript?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Checks if the variant picker structure is the same (quantity-only change).
   * @param {HTMLElement} newVariantPicker - The new variant picker element.
   * @returns {boolean} True if structure is the same, false otherwise.
   */
  #isStructureSame(newVariantPicker) {
    if (!(newVariantPicker instanceof HTMLElement)) return false;

    // Compare the structure by checking:
    // 1. Same number of option containers
    // 2. Same option values in each container
    const currentOptions = this.querySelectorAll('.variant-option');
    const newOptions = newVariantPicker.querySelectorAll('.variant-option');

    if (currentOptions.length !== newOptions.length) return false;

    // Check each option container
    for (let i = 0; i < currentOptions.length; i++) {
      const currentOption = currentOptions[i];
      const newOption = newOptions[i];

      if (!currentOption || !newOption) return false;

      // Compare option names
      const currentName = currentOption instanceof HTMLElement 
        ? currentOption.dataset.optionName || currentOption.getAttribute('data-option-name')
        : currentOption.getAttribute('data-option-name');
      const newName = newOption instanceof HTMLElement
        ? newOption.dataset.optionName || newOption.getAttribute('data-option-name')
        : newOption.getAttribute('data-option-name');
      if (currentName !== newName) return false;

      // Compare number of option values
      const currentInputs = currentOption.querySelectorAll('input[type="radio"], select option');
      const newInputs = newOption.querySelectorAll('input[type="radio"], select option');
      if (currentInputs.length !== newInputs.length) return false;

      // Compare option value IDs
      const currentValueIds = Array.from(currentInputs).map(input => {
        if (input instanceof HTMLInputElement) return input.dataset.optionValueId || input.value;
        if (input instanceof HTMLOptionElement) return input.dataset.optionValueId || input.value;
        return null;
      }).filter(Boolean).sort();

      const newValueIds = Array.from(newInputs).map(input => {
        if (input instanceof HTMLInputElement) return input.dataset.optionValueId || input.value;
        if (input instanceof HTMLOptionElement) return input.dataset.optionValueId || input.value;
        return null;
      }).filter(Boolean).sort();

      if (currentValueIds.length !== newValueIds.length) return false;
      if (currentValueIds.some((id, idx) => id !== newValueIds[idx])) return false;
    }

    return true;
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Check if structure is the same (quantity-only change)
    // If so, skip morphing to prevent blinking, but still update selected state and data
    if (newVariantPickerSource instanceof HTMLElement && this.#isStructureSame(newVariantPickerSource)) {
      // Structure is the same - update script tags with new variant data
      // This is needed for quantity breaks and other scripts that read variant data
      const currentScripts = this.querySelectorAll('script[data-all-variants], script[data-product-options]');
      const newScripts = newVariantPickerSource.querySelectorAll('script[data-all-variants], script[data-product-options]');
      
      // Update script tag contents if they exist
      for (let i = 0; i < Math.min(currentScripts.length, newScripts.length); i++) {
        const currentScript = currentScripts[i];
        const newScript = newScripts[i];
        if (currentScript && newScript && currentScript.textContent !== newScript.textContent) {
          currentScript.textContent = newScript.textContent;
        }
      }
      
      // Also update availability states on inputs (disabled/available)
      const currentInputs = this.querySelectorAll('input[type="radio"], select option');
      const newInputs = newVariantPickerSource.querySelectorAll('input[type="radio"], select option');
      
      // Create a map of new input states by option value ID
      const newInputStates = new Map();
      newInputs.forEach(input => {
        if (!(input instanceof HTMLInputElement || input instanceof HTMLOptionElement)) return;
        
        const optionValueId = input instanceof HTMLInputElement 
          ? input.dataset.optionValueId || input.value
          : input.dataset.optionValueId || input.value;
        if (optionValueId) {
          newInputStates.set(optionValueId, {
            disabled: input.disabled,
            available: input.dataset.optionAvailable === 'true',
            variantId: input.dataset.variantId
          });
        }
      });
      
      // Update current inputs with new states
      currentInputs.forEach(input => {
        const optionValueId = input instanceof HTMLInputElement 
          ? input.dataset.optionValueId || input.value
          : input instanceof HTMLOptionElement
          ? input.dataset.optionValueId || input.value
          : null;
        if (optionValueId && newInputStates.has(optionValueId)) {
          const newState = newInputStates.get(optionValueId);
          if (!newState) return;
          
          if (input instanceof HTMLInputElement) {
            input.disabled = newState.disabled;
            if (newState.available !== undefined) {
              input.dataset.optionAvailable = newState.available ? 'true' : 'false';
            }
            if (newState.variantId) {
              input.dataset.variantId = newState.variantId;
            }
          } else if (input instanceof HTMLOptionElement) {
            input.disabled = newState.disabled;
            if (newState.available !== undefined) {
              input.dataset.optionAvailable = newState.available ? 'true' : 'false';
            }
            if (newState.variantId) {
              input.dataset.variantId = newState.variantId;
            }
          }
        }
      });
      
      // This prevents blinking while still allowing price/availability updates via variant:update event
      // The variant:update event will be dispatched below, which will update prices
      return newProduct;
    }

    // Structure changed - proceed with normal morphing
    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
